/**
 * MapVerticalLegend (Sprint 2026-21, Task 10) — draggable map legend with
 * 5 visibility states + system culture palette. Reads from useMapStoreV2,
 * dispatches SET_LEGEND_POSITION on drag.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md
 *   §9.1 — render rules per (activeRasterMode, cropFillOn) combinatorics:
 *     none + cropFillOn=false → null (hidden)
 *     none + cropFillOn=true  → CulturePalette + status patterns
 *     ndvi / avg_ndvi          → Палитра 1 (NDVI absolute, 13 stops + black)
 *     ndvi_contrast            → Палитра 2 (7 relative classes + <0.20)
 *     uniformity               → Палитра 3 (CV 5 stops)
 *     gnr                      → cloud-pill only (false-color self-explains)
 *   §13 founder lock-in #5 — draggable, position persisted in
 *     state.legendPosition (x,y px from viewport top-left), clamped to
 *     viewport bounds on drag end (invariant I-10).
 *   §13 founder lock-in #6 — system culture colors → CulturePalette.tsx.
 *
 * Drag behaviour (founder spec §13 lock-in #5):
 *   - Mouse-down on the drag handle (top header) starts drag.
 *   - Mousemove dispatches SET_LEGEND_POSITION with clamped (x,y).
 *   - Clamp keeps the legend's top-left within
 *     [0, window.innerWidth - LEGEND_MIN_W] × [0, window.innerHeight - LEGEND_MIN_H]
 *     so the user can never lose the widget off-screen.
 *   - Mouse-up ends the drag (no separate "drag end" persist — every move
 *     dispatches; persistence is the V2 store's responsibility).
 *
 * Cloud color: #2DD4BF (Tailwind teal-400). AssistAgro IP separation —
 * magenta was theirs, we use a distinct hue. Changed from #A855F7 (violet)
 * 2026-06-07: violet маскировался на фиолетовых тенях satellite-снимка.
 * Sync с `backend/src/ikolhoz/rasters/palettes.py:_cloud_lut`.
 */
import { GripVertical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { CulturePalette } from './CulturePalette';
// AK-211: легенда слоя «Рельеф» — 4 класса уклона + «нет данных».
import { SLOPE_LEGEND_ROWS } from '@/lib/colorMaps/slopeColors';
// R4: единый источник NDVI-палитр (absolute + contrast) — устраняет дрейф
// с FieldNdviLegend, ранее stops дублировались в обоих файлах.
import {
  NDVI_STOPS,
  NDVI_CONTRAST_STOPS,
  NDVI_BELOW_THRESHOLD_COLOR,
} from './ndviPalette';

const CLOUD_COLOR = '#2DD4BF';

// Viewport-clamp guards: minimum dimensions of the legend widget (used to
// compute the bottom-right bound in clampToViewport). Conservative — actual
// rendered widget can be smaller, but clamping to a generous box prevents
// the entire widget from sliding off-screen even if the user drags fast.
const LEGEND_MIN_W = 200;
const LEGEND_MIN_H = 100;

interface LegendStop {
  color: string;
  label: string;
}

// Палитра 1 (NDVI absolute / NDVI среднее). 13 stops + black <0.20.
// R4: derived from shared NDVI_STOPS (single source with ndviPalette.ts) —
// `threshold` там несёт данные для заливки, здесь легенде нужен только label.
const PALETTE_1_STOPS: LegendStop[] = NDVI_STOPS.map(({ color, label }) => ({
  color,
  label,
}));

// Палитра 2 (NDVI Контрастный) — 7 относительных классов на [0.2, 1.0]
// per-field stretch. Чёрный <0.20 — фиксированный hard-cutoff (почва/вода).
// R4: shared NDVI_CONTRAST_STOPS (single source with FieldNdviLegend).
const PALETTE_2_STOPS: LegendStop[] = NDVI_CONTRAST_STOPS;

// Палитра 3 (Однородность / CV). 5 stops 0..0.4+, зелёный = однородно.
const PALETTE_3_STOPS: Array<LegendStop & { extraNote?: string }> = [
  { color: '#1a9850', label: '0.00', extraNote: 'однород.' },
  { color: '#91cf60', label: '0.10' },
  { color: '#fee08b', label: '0.20' },
  { color: '#fc8d59', label: '0.30' },
  { color: '#d73027', label: '0.40+', extraNote: 'пёстро' },
];

function clampToViewport(x: number, y: number): { x: number; y: number } {
  // SSR / jsdom без window — fall back to identity. Tests that need clamp
  // assertions should set window.innerWidth/innerHeight via Object.defineProperty.
  if (typeof window === 'undefined') return { x, y };
  const maxX = Math.max(0, window.innerWidth - LEGEND_MIN_W);
  const maxY = Math.max(0, window.innerHeight - LEGEND_MIN_H);
  return {
    x: Math.max(0, Math.min(maxX, x)),
    y: Math.max(0, Math.min(maxY, y)),
  };
}

function LegendCloudRow() {
  return (
    <div className="border-t border-border/60 py-1">
      <div className="flex items-center gap-1 px-2 py-1">
        <span
          className="w-3.5 h-3.5 rounded-sm shrink-0"
          style={{ background: CLOUD_COLOR }}
          aria-hidden
        />
        <span className="text-xs text-foreground font-medium">Облака</span>
      </div>
    </div>
  );
}

interface DragHandleProps {
  onMouseDown: (e: React.MouseEvent) => void;
}

function DragHandle({ onMouseDown }: DragHandleProps) {
  return (
    <div
      data-testid="legend-drag-handle"
      onMouseDown={onMouseDown}
      // Tailwind `cursor-move` — visual affordance. Header background slightly
      // tinted for "this strip is the drag target" discoverability.
      className="cursor-move bg-muted/50 hover:bg-muted/70 px-2 py-1 border-b border-border/60 flex items-center justify-center select-none"
      role="button"
      aria-label="Переместить легенду"
      tabIndex={-1}
    >
      {/* GripVertical, а не «⋮⋮⋮» текстом 8px: та же функция на том же
          экране, что и у ленты снимков, — значит и глиф тот же
          (design review 18.08, зона 1 · А10). */}
      <GripVertical size={14} aria-hidden className="text-muted-foreground" />
    </div>
  );
}

export function MapVerticalLegend() {
  const activeRasterMode = useMapStore((s) => s.activeRasterMode);
  const cropFillOn = useMapStore((s) => s.cropFillOn);
  // AK-211: слой «Рельеф» — своя легенда, приоритет над crop-палитрой
  // (терраин прячет заливку по культуре на уровне registry-предикатов).
  const terrainOn = useMapStore((s) => s.terrainOn);
  const legendPosition = useMapStore((s) => s.legendPosition);
  const dispatch = useMapStore((s) => s.dispatch);

  // dragOffset is non-null while dragging. Stores the pixel delta between
  // the mouse position and the widget's top-left at drag start, so subsequent
  // mousemove events can compute the new top-left as (clientX - offset.x).
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    setDragOffset({
      x: e.clientX - legendPosition.x,
      y: e.clientY - legendPosition.y,
    });
    e.preventDefault();
  };

  useEffect(() => {
    if (!dragOffset) return;
    const onMove = (e: MouseEvent) => {
      const { x, y } = clampToViewport(
        e.clientX - dragOffset.x,
        e.clientY - dragOffset.y,
      );
      dispatch({ type: 'SET_LEGEND_POSITION', x, y });
    };
    const onUp = () => setDragOffset(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragOffset, dispatch]);

  // ── Render rules per spec §9.1 (+ AK-211 terrain) ───────────────────────
  // Терраин активен iff terrainOn && raster='none' (см. registry terrain-fill
  // предикат). (1) прячем легенду только когда НЕТ terrain, raster='none' и
  // crop-fill выключен.
  const terrainActive = terrainOn && activeRasterMode === 'none';
  if (activeRasterMode === 'none' && !cropFillOn && !terrainActive) return null;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: legendPosition.x,
    top: legendPosition.y,
    zIndex: 20,
  };
  const shellClasses =
    'bg-card/95 backdrop-blur rounded-lg shadow-md border border-border';

  // (0) AK-211 «Рельеф» — приоритет над crop-палитрой (терраин прячет заливку
  // по культуре). 4 класса уклона + строка «нет данных».
  if (terrainActive) {
    return (
      <div
        data-testid="map-vertical-legend"
        style={{ ...containerStyle, width: 170 }}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <div className="px-2 py-1 border-b border-border/60 text-xs text-center font-semibold text-foreground">
          Уклон средний
        </div>
        <div className="py-1">
          {SLOPE_LEGEND_ROWS.map((s) => (
            <div key={s.label} className="flex items-center gap-1 px-2 py-1">
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0"
                style={{ background: s.color }}
                aria-hidden
              />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // (2) raster='none' AND cropFillOn=true → CulturePalette + status patterns.
  if (activeRasterMode === 'none' && cropFillOn) {
    return (
      <div
        data-testid="map-vertical-legend"
        style={{ ...containerStyle, width: 160 }}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <CulturePalette />
      </div>
    );
  }

  // (3) GNR — false-color, no palette. Drag-handle + minimal cloud-pill
  // (founder still wants to know that purple = clouds).
  if (activeRasterMode === 'gnr') {
    return (
      <div
        data-testid="map-vertical-legend"
        style={containerStyle}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <div className="px-2 py-2 flex items-center gap-2">
          <span
            className="w-3.5 h-3.5 rounded-sm shrink-0"
            style={{ background: CLOUD_COLOR }}
            aria-hidden
          />
          <span className="text-xs text-foreground font-medium">Облака</span>
        </div>
      </div>
    );
  }

  // (4) Палитра 1 — NDVI / NDVI среднее.
  if (activeRasterMode === 'ndvi' || activeRasterMode === 'avg_ndvi') {
    return (
      <div
        data-testid="map-vertical-legend"
        style={{ ...containerStyle, width: 110 }}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <div className="px-2 py-1 border-b border-border/60 text-xs text-center font-semibold text-foreground">
          NDVI
        </div>
        <div className="py-1">
          {PALETTE_1_STOPS.map((s) => (
            <div key={s.label} className="flex items-center gap-1 px-2 py-1">
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0"
                style={{ background: s.color }}
                aria-hidden
              />
              <span className="text-xs font-mono text-muted-foreground">
                {s.label}
              </span>
            </div>
          ))}
        </div>
        <LegendCloudRow />
      </div>
    );
  }

  // (5) Палитра 2 — NDVI Контрастный (7 классов + <0.20 hard-cutoff).
  if (activeRasterMode === 'ndvi_contrast') {
    return (
      <div
        data-testid="map-vertical-legend"
        style={{ ...containerStyle, width: 190 }}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <div className="px-2 py-1 border-b border-border/60 text-xs text-center font-semibold text-foreground">
          Контрастный
        </div>
        <div className="py-1">
          {PALETTE_2_STOPS.map((s, i) => {
            const isMid = i === 3;
            return (
              <div
                key={s.label}
                className={`flex items-center gap-1 px-2 py-1 ${isMid ? 'bg-accent/40' : ''}`}
              >
                <span
                  className="w-3.5 h-3.5 rounded-sm shrink-0"
                  style={{ background: s.color }}
                  aria-hidden
                />
                <span
                  className={`text-xs ${
                    isMid
                      ? 'text-foreground font-semibold'
                      : 'text-muted-foreground'
                  }`}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
        <div className="border-t border-border/60 py-1">
          <div className="flex items-center gap-1 px-2 py-1">
            <span
              className="w-3.5 h-3.5 rounded-sm shrink-0"
              style={{ background: NDVI_BELOW_THRESHOLD_COLOR }}
              aria-hidden
            />
            <span className="text-xs text-muted-foreground">
              &lt;0.20 (почва/вода)
            </span>
          </div>
        </div>
        <LegendCloudRow />
      </div>
    );
  }

  // (6) Палитра 3 — Однородность.
  if (activeRasterMode === 'uniformity') {
    return (
      <div
        data-testid="map-vertical-legend"
        style={{ ...containerStyle, width: 160 }}
        className={shellClasses}
      >
        <DragHandle onMouseDown={onMouseDown} />
        <div className="px-2 py-1 border-b border-border/60 text-xs text-center font-semibold text-foreground">
          Однородность
        </div>
        <div className="py-1">
          {PALETTE_3_STOPS.map((s) => (
            <div key={s.label} className="flex items-center gap-1 px-2 py-1">
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0"
                style={{ background: s.color }}
                aria-hidden
              />
              <span className="text-xs font-mono text-muted-foreground">
                {s.label}
              </span>
              {s.extraNote && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {s.extraNote}
                </span>
              )}
            </div>
          ))}
        </div>
        <LegendCloudRow />
      </div>
    );
  }

  return null;
}

export default MapVerticalLegend;
